import React from 'react';
import { ShieldCheck, ShieldAlert, AlertTriangle, Activity, Wifi, BatteryCharging, CheckCircle2, Info } from 'lucide-react';

/**
 * DataQualityCard (NousMetrics v2.5)
 * Visualiza la fiabilidad instrumental y calidad de telemetría de sensores
 * Compatible al 100% con Safari WebKit / iOS (sin mix-blend-mode).
 */
export default function DataQualityCard({ quality, className = '' }) {
  if (!quality) return null;

  const components = quality.components || quality.metrics || {};
  const hasSensors = Object.keys(components).length > 0;
  const score = quality.globalScore ?? (quality.overallConfidence != null ? Math.round(quality.overallConfidence * 100) : (hasSensors ? 85 : 0));
  const tier = quality.confidenceTier || (!hasSensors ? 'NO_DATA' : score >= 80 ? 'HIGH' : score >= 60 ? 'MEDIUM' : 'LOW');
  const flags = quality.flags || [];

  const tierConfig = {
    HIGH: {
      color: '#10b981',
      bg: 'rgba(16, 185, 129, 0.12)',
      border: 'rgba(16, 185, 129, 0.3)',
      label: 'Alta Confianza Instrumental',
      desc: 'Telemetría limpia, sin micro-cortes ni artefactos significativos'
    },
    MEDIUM: {
      color: '#f59e0b',
      bg: 'rgba(245, 158, 11, 0.12)',
      border: 'rgba(245, 158, 11, 0.3)',
      label: 'Confianza Moderada',
      desc: 'Interpolación leve o cobertura de sensores parcial'
    },
    LOW: {
      color: '#ef4444',
      bg: 'rgba(239, 68, 68, 0.12)',
      border: 'rgba(239, 68, 68, 0.3)',
      label: 'Confianza Reducida',
      desc: 'Pérdida de señal o lectura wearable con alta dispersión'
    },
    NO_DATA: {
      color: '#94a3b8',
      bg: 'rgba(148, 163, 184, 0.12)',
      border: 'rgba(148, 163, 184, 0.3)',
      label: 'Sin Sensores Activos',
      desc: 'No se detecta telemetría de sensores en la sesión de hoy'
    }
  }[tier] || {
    color: '#10b981',
    bg: 'rgba(16, 185, 129, 0.12)',
    border: 'rgba(16, 185, 129, 0.3)',
    label: 'Alta Confianza',
    desc: 'Telemetría validada'
  };

  const metricLabels = {
    hr: 'Pulso Cardíaco',
    power: 'Potenciómetro',
    gps: 'GPS / Trayectoria',
    cadence: 'Cadencia',
    sleep: 'Monitorización Sueño',
    spo2: 'Saturación SpO2',
    rhr: 'Pulso Reposo (FCR)',
    hrv: 'Variabilidad (HRV)'
  };

  return (
    <div 
      className={`glass-panel ${className}`} 
      style={{
        borderRadius: '16px',
        padding: '1.25rem',
        background: 'rgba(15, 23, 42, 0.75)',
        border: `1px solid ${tierConfig.border}`,
        boxSizing: 'border-box'
      }}
    >
      {/* Header Bento */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '10px',
            background: tierConfig.bg,
            border: `1px solid ${tierConfig.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <ShieldCheck size={18} color={tierConfig.color} />
          </div>
          <div>
            <div style={{ fontSize: '0.92rem', fontWeight: 700, color: '#f8fafc' }}>
              Calidad de Telemetría
            </div>
            <div style={{ fontSize: '0.74rem', color: 'rgba(255,255,255,0.6)' }}>
              Data Confidence Layer 2.0
            </div>
          </div>
        </div>

        {/* Global Badge */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          background: tierConfig.bg,
          border: `1px solid ${tierConfig.border}`,
          borderRadius: '999px',
          padding: '4px 10px'
        }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 800, color: tierConfig.color }}>
            {score}%
          </span>
          <span style={{ fontSize: '0.7rem', fontWeight: 700, color: tierConfig.color, textTransform: 'uppercase' }}>
            {tier}
          </span>
        </div>
      </div>

      <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.7)', marginBottom: '1rem', lineHeight: 1.4 }}>
        {tierConfig.desc}
      </div>

      {/* Grid de Sensores */}
      {!hasSensors ? (
        <div style={{
          fontSize: '0.78rem',
          color: 'rgba(255, 255, 255, 0.55)',
          padding: '0.85rem',
          textAlign: 'center',
          background: 'rgba(255, 255, 255, 0.03)',
          border: '1px dashed rgba(255, 255, 255, 0.1)',
          borderRadius: '10px',
          marginBottom: '0.85rem'
        }}>
          No se registran canales telemétricos con sensores activos en la sesión actual.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '0.65rem', marginBottom: '0.85rem' }}>
          {Object.entries(components).map(([key, item]) => {
            if (!item) return null;
            const pct = Math.round((item.confidenceScore ?? item.confidence ?? 0.8) * 100);
            const barColor = pct >= 80 ? '#10b981' : pct >= 60 ? '#f59e0b' : '#ef4444';
            const label = metricLabels[key] || item.label || key.toUpperCase();

            return (
              <div 
                key={key} 
                style={{
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.06)',
                  borderRadius: '10px',
                  padding: '0.65rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.35rem'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.72rem', color: 'rgba(255,255,255,0.7)' }}>
                  <span style={{ fontWeight: 600 }}>{label}</span>
                  <span style={{ fontWeight: 700, color: barColor }}>{pct}%</span>
                </div>
                <div style={{ width: '100%', height: '4px', background: 'rgba(255,255,255,0.08)', borderRadius: '2px', overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: barColor, borderRadius: '2px', transition: 'width 0.3s ease' }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Flags y Anomalías */}
      {flags.length > 0 && (
        <div style={{
          background: 'rgba(245, 158, 11, 0.08)',
          border: '1px solid rgba(245, 158, 11, 0.2)',
          borderRadius: '8px',
          padding: '0.6rem 0.8rem',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '8px'
        }}>
          <AlertTriangle size={15} color="#f59e0b" style={{ flexShrink: 0, marginTop: '2px' }} />
          <div style={{ fontSize: '0.74rem', color: 'rgba(255,255,255,0.85)', lineHeight: 1.35 }}>
            <span style={{ fontWeight: 700, color: '#f59e0b' }}>Aviso de sensor: </span>
            {flags.join(' • ')}
          </div>
        </div>
      )}
    </div>
  );
}
